import React, { useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ContentBlocks from './ContentBlocks';
import { ContentBlock } from '../../types/content';

export type StripeClasses = {
  box: string;
  chevron: string;
  hover: string;
  title: string;
  muted: string;
  divider: string;
  dot: string;
};

export const moduleStripeClass = (_index = 0): StripeClasses => ({
  box: 'rounded-2xl bg-gray-100 border border-gray-200 overflow-hidden',
  chevron: 'text-gray-600',
  hover: 'hover:bg-gray-200/70',
  title: 'text-gray-900',
  muted: 'text-gray-600',
  divider: 'border-gray-200',
  dot: 'bg-gray-400',
});

export const topicStripeClass = (_moduleIndex = 0, _topicIndex = 0): StripeClasses => ({
  box: 'rounded-xl bg-gray-50 border border-gray-200 overflow-hidden',
  chevron: 'text-gray-600',
  hover: 'hover:bg-gray-100',
  title: 'text-gray-900',
  muted: 'text-gray-600',
  divider: 'border-gray-100',
  dot: 'bg-gray-400',
});

export const selectedTopicStripeClass = (): StripeClasses => ({
  box: 'rounded-xl bg-primary-50 border border-primary-200 overflow-hidden ring-1 ring-primary-200',
  chevron: 'text-primary-600',
  hover: 'hover:bg-primary-100/70',
  title: 'text-primary-900',
  muted: 'text-primary-700',
  divider: 'border-primary-100',
  dot: 'bg-primary-400',
});

export const CurriculumCard: React.FC<{
  open: boolean;
  delayMs?: number;
  includeNeighbors?: boolean;
  scrollOnOpen?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ open, delayMs = 0, includeNeighbors = false, scrollOnOpen = true, className, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(open);
  useEffect(() => {
    const previously = wasOpen.current;
    wasOpen.current = open;
    if (!open || previously || !scrollOnOpen) return;
    const timer = window.setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      if (includeNeighbors) {
        const prev = el.previousElementSibling as HTMLElement | null;
        const topEl = prev || el;
        const top = topEl.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      el.focus({ preventScroll: true });
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [open, delayMs, includeNeighbors, scrollOnOpen]);
  return (
    <div ref={ref} tabIndex={-1} className={`scroll-mt-24 outline-none ${className || ''}`}>
      {children}
    </div>
  );
};

interface CollapsiblePanelProps {
  open: boolean;
  onToggle: () => void;
  tone: 'module' | 'topic';
  stripeIndex?: number;
  topicIndex?: number;
  header: React.ReactNode;
  children?: React.ReactNode;
}

export const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
  open,
  onToggle,
  tone,
  stripeIndex = 0,
  topicIndex = 0,
  header,
  children,
}) => {
  const isModule = tone === 'module';
  const stripe = isModule ? moduleStripeClass(stripeIndex) : topicStripeClass(stripeIndex, topicIndex);
  return (
    <CurriculumCard open={open} delayMs={isModule ? 0 : 80} includeNeighbors={!isModule} className={stripe.box}>
      <div className={`flex items-start gap-2 ${isModule ? 'px-4 py-3' : 'px-3 py-2.5'}`}>
        <button
          type="button"
          aria-expanded={open}
          className={`mt-1 shrink-0 ${stripe.chevron}`}
          onClick={onToggle}
        >
          {open
            ? <ChevronDown className={isModule ? 'h-5 w-5' : 'h-4 w-4'} />
            : <ChevronRight className={isModule ? 'h-5 w-5' : 'h-4 w-4'} />}
        </button>
        <div className="flex-1 min-w-0">{header}</div>
      </div>
      {open && children ? (
        <div className={isModule ? 'px-3 pb-4 space-y-3' : 'px-3 pb-3 space-y-3 border-t border-gray-100 pt-3 bg-white'}>
          {children}
        </div>
      ) : null}
    </CurriculumCard>
  );
};

export interface CurriculumPreviewTopic {
  title: string;
  objective?: string | null;
  estimatedMinutes?: number;
  blocks?: ContentBlock[];
}

export interface CurriculumPreviewModule {
  title: string;
  goal?: string | null;
  topics: CurriculumPreviewTopic[];
}

interface CurriculumPreviewProps {
  modules: CurriculumPreviewModule[];
  openModuleIds: number[];
  openTopicKeys: string[];
  onToggleModule: (index: number) => void;
  onToggleTopic: (key: string) => void;
  onToggleAll?: () => void;
  allExpanded?: boolean;
}

export const CurriculumPreview: React.FC<CurriculumPreviewProps> = ({
  modules,
  openModuleIds,
  openTopicKeys,
  onToggleModule,
  onToggleTopic,
  onToggleAll,
  allExpanded,
}) => (
  <div className="space-y-4">
    {onToggleAll && (
      <div className="flex justify-end">
        <button type="button" className="text-sm font-medium text-primary-600" onClick={onToggleAll}>
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
    )}
    {modules.map((module, mi) => {
      const moduleStripe = moduleStripeClass(mi);
      return (
        <CollapsiblePanel
          key={mi}
          tone="module"
          stripeIndex={mi}
          open={openModuleIds.includes(mi)}
          onToggle={() => onToggleModule(mi)}
          header={
            <div>
              <p className={`font-semibold ${moduleStripe.title}`}>{String(mi + 1).padStart(2, '0')} {module.title || 'Untitled module'}</p>
              <p className={`text-sm mt-1 ${moduleStripe.muted}`}>{module.topics.length} topics</p>
            </div>
          }
        >
          {module.goal ? <p className={`text-sm px-1 ${moduleStripe.muted}`}>{module.goal}</p> : null}
          {module.topics.map((topic, ti) => {
            const key = `${mi}:${ti}`;
            const topicStripe = topicStripeClass(mi, ti);
            return (
              <CollapsiblePanel
                key={key}
                tone="topic"
                stripeIndex={mi}
                topicIndex={ti}
                open={openTopicKeys.includes(key)}
                onToggle={() => onToggleTopic(key)}
                header={
                  <div>
                    <p className={`text-sm font-medium ${topicStripe.title}`}>{topic.title || 'Untitled topic'}</p>
                    {topic.estimatedMinutes ? (
                      <p className={`text-xs mt-0.5 ${topicStripe.muted}`}>{topic.estimatedMinutes} min</p>
                    ) : null}
                  </div>
                }
              >
                {topic.objective ? <p className={`text-sm ${topicStripe.muted}`}>{topic.objective}</p> : null}
                {(topic.blocks || []).length > 0 ? <ContentBlocks blocks={topic.blocks || []} /> : null}
              </CollapsiblePanel>
            );
          })}
        </CollapsiblePanel>
      );
    })}
  </div>
);

export function toggleId(current: number[], id: number) {
  return current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
}

export function toggleKey(current: string[], key: string) {
  return current.includes(key) ? current.filter((x) => x !== key) : [...current, key];
}

export function toggleExclusiveId(current: number[], id: number) {
  return current.length === 1 && current[0] === id ? [] : [id];
}

export function toggleExclusiveKey(current: string[], key: string) {
  return current.length === 1 && current[0] === key ? [] : [key];
}
